import { decodeString } from "@findemdev/findem-helpers";
import { CRED_GRID_LAYOUTS, isSupportedCredGridPair, resolveCredGridFormat } from "./gridFormats";
import { CredGridFormat } from "./layoutTypes";

type NormalizeLegacyGridSelectionParams = {
  rowsPerSlide: number | undefined;
  colsPerRow: number | undefined;
  tileCount: number;
};

type NormalizedLegacyGridSelection = {
  format: CredGridFormat;
  rowsPerSlide: number;
  colsPerRow: number;
};

const DEFAULT_FORMAT: CredGridFormat = "D";
const DEFAULT_MAX_LEGACY_GRID_TEMPLATE_AREAS_LENGTH = 100_000;
const DEFAULT_MAX_LEGACY_GRID_TILE_ID_COUNT = 500;
const DEFAULT_MAX_LEGACY_GRID_TILE_ID_LENGTH = 10_000;

const getLayoutCapacity = ({
  layout,
}: {
  layout: typeof CRED_GRID_LAYOUTS[CredGridFormat];
}) => layout.rows * layout.columns;

const layoutsByCapacity = Object.values(CRED_GRID_LAYOUTS)
  .sort((layoutA, layoutB) => getLayoutCapacity({ layout: layoutA }) - getLayoutCapacity({ layout: layoutB }));

const escapeRegExp = ({ value }: { value: string }) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const getDefaultSelection = (): NormalizedLegacyGridSelection => {
  const layout = CRED_GRID_LAYOUTS[DEFAULT_FORMAT];

  return {
    colsPerRow: layout.columns,
    format: DEFAULT_FORMAT,
    rowsPerSlide: layout.rows,
  };
};

const getClosestRowLayout = ({
  layouts,
  rowsPerSlide,
}: {
  layouts: Array<typeof CRED_GRID_LAYOUTS[CredGridFormat]>;
  rowsPerSlide: number;
}) => layouts.reduce((closestLayout, layout) => {
  const currentDistance = Math.abs(layout.rows - rowsPerSlide);
  const closestDistance = Math.abs(closestLayout.rows - rowsPerSlide);

  if (currentDistance === closestDistance) {
    return getLayoutCapacity({ layout }) > getLayoutCapacity({ layout: closestLayout }) ? layout : closestLayout;
  }

  return currentDistance < closestDistance ? layout : closestLayout;
}, layouts[0]);

export const getUniqueLegacyGridTileIds = ({
  gridTemplateAreas,
  maxGridTemplateAreasLength = DEFAULT_MAX_LEGACY_GRID_TEMPLATE_AREAS_LENGTH,
  maxTileIdLength = DEFAULT_MAX_LEGACY_GRID_TILE_ID_LENGTH,
  maxTileIds = DEFAULT_MAX_LEGACY_GRID_TILE_ID_COUNT,
  prefix,
}: {
  gridTemplateAreas: string | undefined;
  maxGridTemplateAreasLength?: number;
  maxTileIdLength?: number;
  maxTileIds?: number;
  prefix: string;
}): string[] => {
  if (
    typeof gridTemplateAreas !== "string"
    || gridTemplateAreas.length > maxGridTemplateAreasLength
  ) {
    return [];
  }

  const decodedGridTemplateAreas = decodeString(gridTemplateAreas);
  if (decodedGridTemplateAreas.length > maxGridTemplateAreasLength) {
    return [];
  }

  const tileIds = decodedGridTemplateAreas
    .replaceAll("'", "")
    .matchAll(new RegExp(`${escapeRegExp({ value: prefix })}([^\\s]+)`, "g"));
  const uniqueTileIds: string[] = [];
  const seenTileIds = new Set<string>();

  for (const match of tileIds) {
    const tileId = match[1];
    if (
      !tileId
      || tileId.length > maxTileIdLength
      || seenTileIds.has(tileId)
    ) {
      continue;
    }

    seenTileIds.add(tileId);
    uniqueTileIds.push(tileId);

    if (uniqueTileIds.length >= maxTileIds) {
      break;
    }
  }

  return uniqueTileIds;
};

export const normalizeLegacyGridSelection = ({
  rowsPerSlide,
  colsPerRow,
  tileCount,
}: NormalizeLegacyGridSelectionParams): NormalizedLegacyGridSelection => {
  if (
    rowsPerSlide !== undefined
    && colsPerRow !== undefined
    && isSupportedCredGridPair({ rows: rowsPerSlide, columns: colsPerRow })
  ) {
    return {
      colsPerRow,
      format: resolveCredGridFormat({ rows: rowsPerSlide, columns: colsPerRow }),
      rowsPerSlide,
    };
  }

  const safeTileCount = Math.max(0, Math.floor(tileCount));
  if (!safeTileCount && !colsPerRow) {
    return getDefaultSelection();
  }

  const sameColumnLayouts = Object.values(CRED_GRID_LAYOUTS)
    .filter((layout) => layout.columns === colsPerRow)
    .sort((layoutA, layoutB) => getLayoutCapacity({ layout: layoutA }) - getLayoutCapacity({ layout: layoutB }));

  if (sameColumnLayouts.length) {
    const layout = rowsPerSlide !== undefined
      ? getClosestRowLayout({ layouts: sameColumnLayouts, rowsPerSlide })
      : (
        safeTileCount
          ? sameColumnLayouts.find((candidateLayout) => getLayoutCapacity({ layout: candidateLayout }) >= safeTileCount)
          : undefined
      ) ?? sameColumnLayouts[0] ?? CRED_GRID_LAYOUTS[DEFAULT_FORMAT];

    return {
      colsPerRow: layout.columns,
      format: layout.format,
      rowsPerSlide: layout.rows,
    };
  }

  if (!safeTileCount) {
    return getDefaultSelection();
  }

  const layout = layoutsByCapacity.find((candidateLayout) => getLayoutCapacity({ layout: candidateLayout }) >= safeTileCount)
    ?? CRED_GRID_LAYOUTS.G;

  return {
    colsPerRow: layout.columns,
    format: layout.format,
    rowsPerSlide: layout.rows,
  };
};
